
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

export default function AITrading() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const tabs = [
    {
      title: 'Market Analysis',
      icon: 'ri-line-chart-fill',
      content: 'AI scans global markets 24/7, analyzing trends and opportunities',
    },
    {
      title: 'Auto-Trading',
      icon: 'ri-exchange-fill',
      content: 'Automated execution based on AI predictions with risk control',
    },
    {
      title: 'Risk Control',
      icon: 'ri-shield-check-fill',
      content: 'Smart stop-loss and portfolio balancing to protect investments',
    },
    {
      title: 'Performance',
      icon: 'ri-dashboard-fill',
      content: 'Live tracking of profits and AI decision analytics',
    },
  ];

  const stats = [
    { label: 'Avg. Win Rate', value: '87%', icon: 'ri-trophy-fill', color: 'text-green-400' },
    { label: 'Trades/Day', value: '1,247', icon: 'ri-exchange-line', color: 'text-cyan-400' },
    { label: 'Response Time', value: '<50ms', icon: 'ri-speed-fill', color: 'text-purple-400' },
    { label: 'Active Users', value: '45.2K', icon: 'ri-group-fill', color: 'text-pink-400' },
  ];

  // Example placeholder plans array
  const plans = [
    { name: 'Basic', popular: false },
    { name: 'Pro', popular: true },
    { name: 'Enterprise', popular: false },
  ];

  const handleSubscribe = (planName: string) => {
    navigate('/payment/coinbase');
  };

  return (
    <section className="py-20 bg-gradient-to-b from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full mb-6 backdrop-blur-sm">
            <i className="ri-robot-2-fill text-purple-400"></i>
            <span className="text-purple-400 text-sm font-semibold">PepBot AI Engine</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black mb-4 text-white">
            Intelligent Auto-Trading System
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Let AI handle your trades while you sleep. PepBot never misses an opportunity.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Trading Interface */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl"></div>
            <div className="relative p-6 bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl border border-gray-700/50 rounded-3xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Live Trading Dashboard</h3>
                <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 border border-green-500/50 rounded-full">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-400 text-sm font-semibold">Active</span>
                </div>
              </div>

              {/* Chart Placeholder */}
              <div className="relative h-48 mb-6 bg-gray-800/50 rounded-2xl overflow-hidden border border-gray-700/50">
                <img
                  src="https://public.readdy.ai/ai/img_res/3972007f28964dd16fbbabce220c9d94.jpg"
                  alt="Trading Chart"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
              </div>

              {/* Recent Trades */}
              <div className="space-y-3">
                {[
                  { pair: 'BTC/USDT', action: 'BUY', price: '$67,234', profit: '+2.4%', time: '2m ago' },
                  { pair: 'ETH/USDT', action: 'SELL', price: '$3,456', profit: '+1.8%', time: '5m ago' },
                  { pair: 'PEP/USDT', action: 'BUY', price: '$0.65', profit: '+45.2%', time: '8m ago' },
                ].map((trade, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl hover:border-purple-500/50 transition-all duration-300">
                    <div className="flex items-center gap-3">
                      <div className={`px-2 py-1 ${trade.action === 'BUY' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'} rounded-lg font-bold text-xs`}>
                        {trade.action}
                      </div>
                      <div>
                        <div className="text-white font-semibold text-sm">{trade.pair}</div>
                        <div className="text-gray-400 text-xs">{trade.time}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-semibold text-sm">{trade.price}</div>
                      <div className="text-green-400 text-xs font-semibold">{trade.profit}</div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate('/trading')}
                className="w-full mt-6 p-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg text-white font-medium hover:scale-105 transition-all duration-300 cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-line-chart-line"></i>
                Start Trading
              </button>
            </div>
          </div>

          {/* Features Tabs */}
          <div className="space-y-6">
            <div className="flex flex-wrap gap-3 mb-6">
              {tabs.map((tab, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTab(idx)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer text-sm ${
                    activeTab === idx
                      ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg shadow-purple-500/50'
                      : 'bg-gray-800/50 text-gray-400 border border-gray-700/50 hover:border-purple-500/50'
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  {tab.title}
                </button>
              ))}
            </div>

            <div className="p-6 bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl border border-gray-700/50 rounded-3xl">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-lg">
                  <i className={`${tabs[activeTab].icon} text-xl text-white`}></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">{tabs[activeTab].title}</h4>
                  <p className="text-gray-400 text-base leading-relaxed">{tabs[activeTab].content}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-6">
                {stats.map((stat, idx) => (
                  <div key={idx} className="p-3 bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <i className={`${stat.icon} ${stat.color} text-sm`}></i>
                      <span className="text-gray-400 text-xs">{stat.label}</span>
                    </div>
                    <div className={`text-lg font-black ${stat.color}`}>{stat.value}</div>
                  </div>
                ))}
              </div>

              {/* Subscription Plans */}
              <div className="mt-6 space-y-3">
                <button
                  onClick={() => handleSubscribe('basic')}
                  disabled={isLoading}
                  className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl font-bold text-white hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Processing...' : 'Subscribe Basic Plan'}
                </button>

                <button
                  onClick={() => handleSubscribe('pro')}
                  disabled={isLoading}
                  className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl font-bold text-white hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Processing...' : 'Subscribe Pro Plan'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl border rounded-2xl shadow-xl p-8 transition-all duration-300 hover:shadow-2xl ${
                plan.popular ? 'border-purple-500/50 relative' : 'border-gray-700/50'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full text-white text-sm font-bold">
                  Most Popular
                </div>
              )}
              <button
                onClick={() => handleSubscribe(plan.name)}
                className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
                  plan.popular
                    ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:scale-105'
                    : 'bg-gray-800 text-white hover:bg-gray-700'
                }`}
              >
                Pay with Crypto
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
